/**
 * SEO Data - Centralized meta tags for all pages
 * Optimized for B2B institutional analytical-reagent supply keywords.
 */

export const SEO_DATA = {
  // ============ HOME PAGE ============
  home: {
    title: 'Analytical-Grade Amino Acid Derivatives for Institutional Laboratories',
    description: 'Amidex supplies certified reagent-grade organic synthesis compounds and custom amino acid chains to verified academic, government, and OEM research institutions. Institutional credentialing required.',
    keywords: 'analytical reagents, amino acid derivatives, organic synthesis compounds, laboratory reagents, institutional supply, B2B reagents, reference standards, custom synthesis',
  },

  // ============ PRODUCTS PAGE ============
  products: {
    title: 'Reagent Catalog - Institutional Access',
    description: 'Browse the Amidex catalog of analytical-grade amino acid derivatives and organic synthesis compounds for verified institutional accounts.',
    keywords: 'reagent catalog, analytical reagents, amino acid derivatives, laboratory compounds, institutional supply, B2B reagents',
    noindex: true,
  },

  // ============ RESEARCH PAGE ============
  research: {
    title: 'Technical Reference Library & Assay Standards',
    description: 'Technical reference materials for institutional laboratories. Storage guidelines, analytical assay standards, and documentation.',
    keywords: 'technical reference, analytical standards, laboratory documentation, assay protocols, institutional research',
  },

  // ============ TDS LIBRARY ============
  coaLibrary: {
    title: 'Technical Data Sheet Library',
    description: 'Access lot-specific technical data sheets for Amidex analytical reagents. Documentation for institutional laboratory records.',
    keywords: 'technical data sheet, assay documentation, analytical grade, lot verification, laboratory records',
  },

  // ============ CONTACT PAGE ============
  contact: {
    title: 'Contact Us - Institutional Inquiries',
    description: 'Contact Amidex for institutional reagent supply inquiries. Verified academic, government, non-profit, and OEM research institutions only.',
    keywords: 'contact Amidex, institutional orders, reagent supply, B2B inquiry, academic research',
  },

  // ============ INSTITUTIONAL ACCESS PAGE ============
  institutionalAccess: {
    title: 'Institutional Credentialing & Access Policy',
    description: 'Amidex institutional credentialing policy. Registration restricted to verified .edu, .gov, and .org institutional email domains. Manual review within 1-2 business days.',
    keywords: 'institutional access, buyer credentialing, B2B reagents, verified accounts, academic registration',
  },

  // ============ LEGAL PAGES ============
  privacy: {
    title: 'Privacy Policy',
    description: 'Amidex privacy policy. How we collect, use, and protect institutional account information.',
    noindex: true,
  },

  terms: {
    title: 'Terms & Conditions',
    description: 'Terms and conditions for institutional reagent purchases from Amidex. Non-clinical laboratory use only.',
    noindex: true,
  },

  shipping: {
    title: 'Fulfillment Policy',
    description: 'Amidex fulfillment policy. Standard 3-5 business day institutional fulfillment SLA.',
    noindex: true,
  },

  refunds: {
    title: 'Returns Policy',
    description: 'Amidex returns policy for institutional reagent orders.',
    noindex: true,
  },

  accessibility: {
    title: 'Accessibility Statement',
    description: 'Amidex commitment to digital accessibility.',
    noindex: true,
  },
};

// ============ PRODUCT-SPECIFIC SEO TEMPLATES ============
export const getProductSEO = (product) => {
  const productName = product.name;
  const category = product.category || 'Analytical Reagent';

  return {
    title: `${productName} - Analytical Reagent`,
    description: `${productName} analytical-grade laboratory reagent for institutional use. ${category}. For verified institutional accounts only.`,
    keywords: `${productName}, analytical reagent, ${category.toLowerCase()}, laboratory compound, institutional supply`,
    noindex: true,
  };
};

// ============ RESEARCH REFERENCE PAGES SEO ============
export const getPeptideResearchPageSEO = (compoundName) => {
  return {
    title: `${compoundName} Technical Reference | Specifications`,
    description: `Technical reference information for ${compoundName}. Structure specifications, storage guidelines, and documentation for institutional laboratories.`,
    keywords: `${compoundName}, technical reference, analytical specifications, laboratory documentation`,
  };
};

// ============ FAQ DATA (compliance-safe) ============
export const RESEARCH_FAQS = [
  {
    question: 'Who can purchase from Amidex?',
    answer: 'Amidex supplies analytical-grade laboratory reagents exclusively to verified institutional accounts, including academic laboratories, government agencies, non-profit research institutes, and industrial OEM buyers. Registration requires an institutional email address and passes a manual credentialing review within 1-2 business days.',
  },
  {
    question: 'What kind of materials do you supply?',
    answer: 'Analytical-grade amino acid derivatives, custom synthesis chains, peptidomimetic reagents, and certified reference standards intended for laboratory processing, method development, and analytical testing.',
  },
  {
    question: 'What is the assay standard of your reagents?',
    answer: 'All Amidex materials are supplied as analytical-grade laboratory reagents. Each lot is characterized by chromatographic assay and mass verification, with a technical data sheet provided for institutional record-keeping.',
  },
  {
    question: 'Do you provide product usage guidance?',
    answer: 'No. Amidex does not provide application, formulation, dosing, or handling guidance. All materials are supplied as technical-grade reagents and must be evaluated and characterized independently by the receiving institution\'s qualified personnel.',
  },
  {
    question: 'How should analytical reagents be stored?',
    answer: 'Materials are supplied in lot units with recommended storage conditions listed on the technical data sheet. Institutional buyers are responsible for storage, handling, and characterization according to their own laboratory protocols.',
  },
  {
    question: 'Are these materials for consumer use?',
    answer: 'No. All Amidex products are technical-grade chemical reagents supplied strictly for industrial laboratory processing, analytical testing, and non-clinical institutional research. They are not intended for consumer use, diagnostic procedures, or therapeutic application.',
  },
  {
    question: 'What testing methods are used?',
    answer: 'Chromatographic assay analysis (HPLC), mass verification (LC-MS), and identity confirmation are performed on each lot. Full technical data sheets are supplied with every shipment for institutional records.',
  },
  {
    question: 'How long does fulfillment take?',
    answer: 'Standard institutional fulfillment SLA is 3-5 business days to verified institutional addresses. All shipments include lot-specific technical data documentation.',
  },
];

// ============ TECHNICAL REFERENCE INFO (public-safe wrappers) ============
export const PEPTIDE_INFO = {};

export default SEO_DATA;
